import React from 'react';
import { Ban, Eye, Pencil, Trash2, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { EmailRule } from '../useData';
import { formatActionLabel, formatConditionLabel, formatMatchLabel } from '../emailRuleLabels';

interface EmailRuleCardProps {
  rule: EmailRule;
  accountLabel: string;
  detailHref: string;
  editHref: string;
  personalFolderNames: Map<number, string>;
  userNames: Map<number, string>;
  onDelete: () => void;
  deleting: boolean;
}

const EmailRuleCard: React.FC<EmailRuleCardProps> = ({
  rule,
  accountLabel,
  detailHref,
  editHref,
  personalFolderNames,
  userNames,
  onDelete,
  deleting,
}) => {
  const { colors } = useTheme();
  const conditions = rule.conditions?.rules ?? [];
  const actions = rule.actions?.actions ?? [];
  const isDisabled = !rule.enabled;
  const visibleConditions = conditions.slice(0, 3);
  const visibleActions = actions.slice(0, 3);
  const hiddenConditionCount = conditions.length - visibleConditions.length;
  const hiddenActionCount = actions.length - visibleActions.length;
  const ruleTitle = rule.name?.trim() || `Rule #${rule.id}`;

  return (
    <article
      className={cn(
        'overflow-hidden rounded-xl border border-l-4 transition-shadow hover:shadow-md',
        colors.border,
        colors.surface,
        isDisabled ? 'border-l-amber-500' : 'border-l-indigo-500',
      )}
    >
      <div className="flex flex-col gap-4 p-4 sm:p-5 lg:flex-row lg:items-start lg:justify-between">
        <Link to={detailHref} className={cn('group flex min-w-0 flex-1 items-start gap-4', isDisabled && 'opacity-90')}>
          <div
            className={cn(
              'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border',
              colors.border,
              isDisabled
                ? 'bg-amber-500/10 text-amber-700 dark:text-amber-300'
                : cn(colors.surfaceHover, 'text-indigo-600 dark:text-indigo-400'),
            )}
          >
            {isDisabled ? <Ban className="h-5 w-5" aria-hidden /> : <Zap className="h-5 w-5" aria-hidden />}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className={cn('truncate text-base font-semibold group-hover:underline', colors.text)}>
                {ruleTitle}
              </h2>
              <span
                className={cn(
                  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium',
                  isDisabled
                    ? 'border-amber-500/30 bg-amber-500/10 text-amber-800 dark:text-amber-300'
                    : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
                )}
              >
                {isDisabled ? 'Disabled' : 'Enabled'}
              </span>
              <span
                className={cn(
                  'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium',
                  colors.border,
                  colors.textMuted,
                )}
              >
                {formatMatchLabel(rule.conditions.match)}
              </span>
            </div>

            {isDisabled ? (
              <p className="mt-1 text-sm text-amber-800 dark:text-amber-300">Not applied during email sync</p>
            ) : null}

            {visibleConditions.length > 0 ? (
              <div className="mt-3">
                <p className={cn('mb-1.5 text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>When</p>
                <ul className={cn('space-y-1 rounded-lg border px-3 py-2 text-xs', colors.border, colors.surfaceHover)}>
                  {visibleConditions.map((condition, index) => (
                    <li key={index} className={cn('flex items-start gap-2', colors.textSecondary)}>
                      <span className="mt-0.5 shrink-0 text-indigo-500 dark:text-indigo-400">•</span>
                      <span className="min-w-0 break-words">{formatConditionLabel(condition)}</span>
                    </li>
                  ))}
                  {hiddenConditionCount > 0 ? (
                    <li className={cn('text-[11px]', colors.textMuted)}>+{hiddenConditionCount} more conditions</li>
                  ) : null}
                </ul>
              </div>
            ) : null}

            {visibleActions.length > 0 ? (
              <div className="mt-3">
                <p className={cn('mb-1.5 text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>Then</p>
                <ul className={cn('space-y-1 rounded-lg border px-3 py-2 text-xs', colors.border, colors.surfaceHover)}>
                  {visibleActions.map((action, index) => (
                    <li key={index} className={cn('flex items-start gap-2', colors.textSecondary)}>
                      <span className="mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400">→</span>
                      <span className="min-w-0 break-words">
                        {formatActionLabel(action, personalFolderNames, userNames)}
                      </span>
                    </li>
                  ))}
                  {hiddenActionCount > 0 ? (
                    <li className={cn('text-[11px]', colors.textMuted)}>+{hiddenActionCount} more actions</li>
                  ) : null}
                </ul>
              </div>
            ) : null}
          </div>
        </Link>

        <div className="flex shrink-0 items-center gap-1 self-end lg:self-start">
          <Link
            to={detailHref}
            title="View rule"
            className={cn(
              'inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors',
              colors.text,
              colors.interactiveHover,
            )}
          >
            <Eye className="h-4 w-4" />
          </Link>
          <Link
            to={editHref}
            title="Edit rule"
            className={cn(
              'inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors',
              colors.text,
              colors.interactiveHover,
            )}
          >
            <Pencil className="h-4 w-4" />
          </Link>
          <button
            type="button"
            title="Delete rule"
            disabled={deleting}
            onClick={onDelete}
            className={cn(
              'inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors',
              'text-rose-600 hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-950/30',
              deleting && 'cursor-not-allowed opacity-50',
            )}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className={cn('grid grid-cols-2 gap-px border-t sm:grid-cols-4', colors.border, colors.surfaceHover)}>
        <StatCell label="Priority" value={String(rule.priority)} />
        <StatCell label="Account" value={accountLabel} />
        <StatCell label="Conditions" value={String(conditions.length)} />
        <StatCell label="Actions" value={String(actions.length)} />
      </div>
    </article>
  );
};

function StatCell({ label, value }: { label: string; value: string }) {
  const { colors } = useTheme();
  return (
    <div className={cn('px-4 py-3', colors.surface)}>
      <p className={cn('text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>{label}</p>
      <p className={cn('mt-1 truncate text-sm font-semibold', colors.text)} title={value}>
        {value}
      </p>
    </div>
  );
}

export default EmailRuleCard;
